import type { SorterResult } from "antd/es/table/interface";
import i18n from "i18next";
import { type FC, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import type { TerminalToCampaignEntity } from "src/app/features/Campaign/types/TerminalToCampaignEntity";
import { getAssignedDataGroup } from "../../../../core/router/auth";
import { ResourceName } from "../../../../core/router/config/ResourceName";
import { withReturnTo } from "../../../../core/router/navigation/returnTo";
import { BASE_PATH, FORM_ACTION } from "../../../../core/router/types/routerTypes";
import type { ID } from "../../../../core/types/fe";
import type { ExtendedColumnsType } from "../../../index";
import { MultiSegmentProgressBar } from "../../../ProgressBar/MultiSegmentProgressBar";
import { PropertyTable } from "../../../Table/components/PropertyTable";
import { displayDateTimeValue, displayEnumValue } from "../../../Table/components/PropertyTable/transformations/cellTransformations";
import { TableSearchBar } from "../../../Table/components/TableSearchBar/TableSearchBar";
import { useTableSearchFilter } from "../../../Table/hooks/useTableSearchFilter";
import type { AntCustomFormField, CommonFieldAttributes } from "../../types/types";
import { useLoadCampaignTerminalsQuery } from "./serverApi";

export interface TerminalsToCampaignsProps extends AntCustomFormField<TerminalToCampaignEntity[]>, CommonFieldAttributes {
    campaignId: ID;
}

const SEARCHABLE_FIELDS: (keyof TerminalToCampaignEntity)[] = [
    "id",
    "terminalId",
    "terminalName",
    "appVersion",
    "confVersion",
    "updateStatus",
    "changed",
];

export const TerminalsToCampaignTable: FC<TerminalsToCampaignsProps> = ({ value, campaignId }) => {
    const [allTerminals, setAllTerminals] = useState<TerminalToCampaignEntity[]>([]);
    const { filter, setFilter, filteredItems: filteredTerminals } = useTableSearchFilter(allTerminals, SEARCHABLE_FIELDS);
    const [refreshIndex, setRefreshIndex] = useState(0);

    const getColumnsConfig = useCallback(
        (_t: Function, sortInfo: SorterResult<TerminalToCampaignEntity>): ExtendedColumnsType<TerminalToCampaignEntity>[] => {
            return [
                {
                    title: i18n.t("terminal.id"),
                    key: "id",
                    dataIndex: "id",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "id" ? sortInfo.order : null,
                },
                {
                    title: i18n.t("terminal.terminalId"),
                    key: "terminalId",
                    dataIndex: "terminalId",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "terminalId" ? sortInfo.order : null,
                },
                {
                    title: i18n.t("terminal.name"),
                    key: "name",
                    dataIndex: "terminalName",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "name" ? sortInfo.order : null,
                },
                {
                    title: i18n.t("campaign.export.applicationVersion"),
                    key: "appVersion",
                    dataIndex: "appVersion",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "appVersion" ? sortInfo.order : null,
                },
                {
                    title: i18n.t("campaign.export.configurationVersion"),
                    key: "confVersion",
                    dataIndex: "confVersion",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "confVersion" ? sortInfo.order : null,
                },
                {
                    title: i18n.t("terminal.status"),
                    key: "updateStatus",
                    dataIndex: "updateStatus",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "updateStatus" ? sortInfo.order : null,
                    render: (cellValue) => displayEnumValue(cellValue, "campaign-update-status"),
                },
                {
                    title: i18n.t("terminal.changed"),
                    key: "changed",
                    dataIndex: "changed",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "changed" ? sortInfo.order : null,
                    render: (cellValue) => displayDateTimeValue(cellValue),
                },
            ];
        },
        [],
    );

    useEffect(() => {
        const interval = setInterval(() => {
            setRefreshIndex((prev) => prev + 1);
        }, 15000); // 15 seconds

        return () => clearInterval(interval);
    }, []);

    const request = {
        dataGroupCode: getAssignedDataGroup()?.code,
        campaignId,
        refreshIndex,
    };

    const { data } = useLoadCampaignTerminalsQuery(request, { refetchOnMountOrArgChange: true, refetchOnReconnect: true });

    const navigate = useNavigate();
    const onRowClick = (record: TerminalToCampaignEntity) => {
        navigate(`${BASE_PATH}/${ResourceName.TERMINALS}/${FORM_ACTION.EDIT}/${record.terminalSystemId}`, withReturnTo(location));
    };

    return (
        <>
            <TableSearchBar value={filter} onChange={setFilter} />
            <MultiSegmentProgressBar value={value} data={data} setTerminals={setAllTerminals} refreshIndex={refreshIndex} />
            {filteredTerminals.length !== 0 && (
                <PropertyTable<TerminalToCampaignEntity>
                    name="terminalsToCampaign_table"
                    getColumnsConfig={getColumnsConfig}
                    tableData={filteredTerminals}
                    propertyAsRowKey="id"
                    readOnly
                    rowsAreSelectable={false}
                    onRow={(record: TerminalToCampaignEntity) => {
                        return {
                            onClick: () => onRowClick(record),
                        };
                    }}
                />
            )}
            {allTerminals.length === 0 && <label>&nbsp;{i18n.t("campaignCreate.affectedTerminalsZero")}</label>}
        </>
    );
};
